import { useNavigate } from "react-router-dom";
import { servicesPageContent, aboutPageStats } from "../data/constants";
import AnimatedCounter from "../components/AnimatedCounter";
import SiteCta from "../components/SiteCta";

const baseUrl = process.env.PUBLIC_URL || "";
const serviceIconsBySlug = {
  "cloud-solution": "bi-cloud-arrow-up-fill",
  "storage-virtualization": "bi-hdd-stack-fill",
  "cybersecurity-solution": "bi-shield-lock-fill",
  "networking-solution": "bi-hdd-network-fill",
  "ai-iot-solutions": "bi-cpu-fill",
  "software-solutions": "bi-code-slash",
};

const serviceHighlights = [
  "Cloud, datacentre & virtualization",
  "Network security & collaboration",
  "AI, automation & managed services",
];

export default function ServicesPage() {
  const navigate = useNavigate();
  return (
    <div className="services-page">
      <section className="services-page-hero animate-on-scroll">
        <div className="container">
          <div className="row g-4 g-lg-5 align-items-center">
            <div className="col-lg-7">
              <span className="services-page-label">What We Offer</span>
              <h1 className="services-page-hero-title">
                Our <span className="services-page-accent">Services</span>
              </h1>
              <p className="services-page-hero-desc">
                Comprehensive IT solutions from infrastructure design to AI integration — all under one roof.
              </p>
            </div>
            <div className="col-lg-5">
              <div className="services-page-stats-card">
                {aboutPageStats.map((stat, i) => (
                  <div
                    key={stat.label}
                    className={`services-page-stat${i % 2 === 0 ? " has-right-border" : ""}${i < 2 ? " has-bottom-border" : ""}`}
                  >
                    <AnimatedCounter
                      value={stat.value}
                      suffix={stat.suffix}
                      className="services-page-stat-num"
                      as="div"
                    />
                    <span className="services-page-stat-label">{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section services-page-grid-section animate-on-scroll">
        <div className="container">
          <div className="text-center mb-5">
            <span className="section-badge">Our Capabilities</span>
            <h2 className="section-title">Solutions Built for Modern Businesses</h2>
            <p className="section-subtitle mx-auto">Explore the same solution stack showcased on our home page, with focused outcomes for each service area.</p>
          </div>
          <div className="row g-4">
            {servicesPageContent.map((s) => (
              <div key={s.title} className="col-md-6 col-lg-4">
                <article
                  className="services-page-card"
                  onClick={() => navigate(`/solutions/${s.slug}`)}
                  onKeyDown={(e) => e.key === "Enter" && navigate(`/solutions/${s.slug}`)}
                  role="link"
                  tabIndex={0}
                  aria-label={`View ${s.title} solution`}
                >
                  <div className="services-page-card-top">
                    <div className="services-page-card-icon">
                      <i className={`bi ${serviceIconsBySlug[s.slug] || "bi-gear-wide-connected"}`}></i>
                    </div>
                    <span className="services-page-card-arrow" aria-hidden="true">
                      <i className="bi bi-arrow-up-right"></i>
                    </span>
                  </div>
                  <h5 className="services-page-card-title">{s.title}</h5>
                  <p className="services-page-card-desc">{s.desc}</p>
                  {s.tags?.length > 0 && (
                    <div className="services-page-card-tags">
                      {s.tags.map((tag) => (
                        <span key={tag} className="services-page-card-tag">{tag}</span>
                      ))}
                    </div>
                  )}
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section services-page-about animate-on-scroll">
        <div className="container">
          <div className="row g-5 align-items-center">
            <div className="col-lg-6 order-lg-2">
              <div className="services-page-about-visual">
                <img
                  src={`${baseUrl}/img/services/our-services.png`}
                  alt="Our Services"
                  className="services-page-about-img"
                  loading="lazy"
                  onError={(e) => { e.target.style.display = "none"; e.target.nextElementSibling?.classList.remove("d-none"); }}
                />
                <div className="services-page-about-placeholder d-none">
                  <i className="bi bi-gear-wide-connected"></i>
                  <span>Our Services</span>
                </div>
              </div>
            </div>
            <div className="col-lg-6 order-lg-1">
              <span className="section-badge">What We Do</span>
              <h2 className="section-title">End-to-End IT Services</h2>
              <p className="services-page-about-text">
                We deliver integrated technology expertise across cloud, datacentre, security, collaboration, and managed services to maximize your business outcomes.
              </p>
              <ul className="services-page-highlights">
                {serviceHighlights.map((item) => (
                  <li key={item}>
                    <i className="bi bi-check-circle-fill" aria-hidden="true"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <SiteCta
        title="Interested in Our Solutions?"
        desc="Learn more about who we are and how we work with organizations like yours."
        buttonText="Contact Us"
        to="/contact"
      />
    </div>
  );
}
